import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react"
import AnimatedTextLoader from "./AnimatedTextLoader"
import Loader from "../../../../components/ui/loader"
import NewCardModal from "./NewCardModal"
import PayPalApplePay from "../../../../components/PayPal/Private/PayPalApplePay"
import PayPalCardFields from "../../../../components/PayPal/Private/PayPalCardFields"
import { Button } from "../../../../components/ui/button"
import { ServiceRequests } from "../../../../api/app.service"
import { PaymentRequests } from "../../../../api/app.service"
import { PayPalButton } from "../../../../components/PayPal/Private/PayPalButton"
import { PayPalGooglePay } from "../../../../components/PayPal/Private/PayPalGooglePay"
import {
  SvgAmericanExpress,
  SvgDiscover,
  SvgMastercard,
  SvgVisa,
  SvgDinersClub,
  SvgChevronLeft,
} from "../../../../components/icons"
import { UserCtx } from "../../../../context/userContext"
import { useToast } from "../../../../hooks/useToast"
import { useLocation, useNavigate } from "react-router-dom"
import { NotificationCtx } from "../../../../context/notificationContext"

const PayNowPage: React.FC = () => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { state } = useLocation()
  const { lastWebsocketMessage } = useContext(NotificationCtx)
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true)
  const [isChecked, setIsChecked] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(false)
  const [isSdkReady, setIsSdkReady] = useState(false)
  const [processingLoading, setProcessingLoading] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [showNewCardModal, setShowNewCardModal] = useState(false)
  const [cardSave, setCardSave] = useState(false)
  const isCheckedRef = useRef(false)
  const navigate = useNavigate()
  const transaction = state?.transaction
  const payments = state?.localPaymentMethods
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID
  const merchantID =
    transaction?.merchantID || process.env.REACT_APP_PAYPAL_PAYER_ID

  const isApplePayEnabled =
    process.env.REACT_APP_PAYPAL_ENABLE_APPLE_PAY === "true"

  const isGooglePayEnabled =
    process.env.REACT_APP_PAYPAL_ENABLE_GOOGLE_PAY === "true"

  const paypalComponents = [
    "buttons",
    "card-fields",
    isApplePayEnabled && "applepay",
    isGooglePayEnabled && "googlepay",
  ]
    .filter(Boolean)
    .join(",")

  const getPaymentMethods = async () => {
    if (!user) return
    setLoadingPaymentMethods(true)
    try {
      const data = await PaymentRequests.getSavedPaymentMethods({
        user_id: parseInt(user.user_id),
      })

      const allCards = (Array.isArray(data?.data) ? data.data : [])
        .flatMap((entry: any) => entry?.payment_tokens || [])
        .filter((token: any) => token?.payment_source?.card)
        .map((token: any) => ({
          id: token.id,
          payment_method_token: token.id,
          ...token.payment_source.card,
        }))
      setPaymentMethods(allCards)
    } catch (ex: any) {
      console.error("Error fetching payment methods:", ex)
      toast.error(
        ex.response?.data?.message || "Failed to load payment methods."
      )
    } finally {
      setLoadingPaymentMethods(false)
    }
  }

  useEffect(() => {
    if (!payments || payments.length === 0) {
      getPaymentMethods()
    }
  }, [user, payments])

  useEffect(() => {
    if (
      lastWebsocketMessage?.type === "payment" &&
      lastWebsocketMessage?.data?.event_type === "card_added"
    ) {
      getPaymentMethods()
    }
  }, [lastWebsocketMessage])

  const createOrder = useCallback(
    async (
      data: any,
      source: "paypal" | "card" | "apple_pay" | "google-pay"
    ) => {
      setProcessingLoading(true)

      const paymentMethodId =
        source === "paypal" ? "paypal" : typeof data === "string" ? data : ""

      try {
        return await ServiceRequests.createPurchaseService({
          user_id: user.user_id,
          coach_user_id: transaction.merchantUserID,
          client_profile_id: user.activeProfileId,
          coach_profile_id: transaction.merchantProfileID,
          payment_method_id:
            paymentMethodId === "new" ||
            paymentMethodId === "paypal" ||
            source === "google-pay" ||
            source === "apple_pay"
              ? ""
              : selectedCardId,
          service: transaction.service_details,
          stripe_product_id: transaction.service_details?.stripe_product_id,
          source,
          save_for_future: true,
          user_environment: "",
          applied_discounts: {
            promo_value: 0,
            order_total: parseInt(transaction.amount),
            order_total_upfront: 0,
            order_total_installment: 0,
          },
        })
          .then((res: any) => res.data)
          .then((order: any) => {
            if (order.status === "COMPLETED") {
              onApprove({ orderID: order.id, paymentMethodId }, source)
              return order.id
            }

            if (order.id) {
              return order.id
            }

            throw new Error("No order id returned from createPurchaseService")
          })
      } catch (error: any) {
        console.error("Error creating PayPal order:", error)
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong while processing your payment."
        )
        setProcessingLoading(false)
        return null
      }
    },
    [user, transaction, toast]
  )

  const onApprove = useCallback(
    async (
      data: any,
      source: "paypal" | "card" | "apple_pay" | "google-pay"
    ) => {
      const paymentMethodId =
        source === "paypal" ? "paypal" : typeof data === "string" ? data : ""
      const orderID = typeof data === "string" ? data : data?.orderID

      try {
        const response = await ServiceRequests.completePurchaseService({
          user_id: parseInt(user.user_id),
          coach_user_id: transaction.merchantUserID,
          client_profile_id: user.activeProfileId,
          coach_profile_id: transaction.merchantProfileID,
          payment_method_id:
            paymentMethodId === "new" ||
            paymentMethodId === "paypal" ||
            source === "google-pay" ||
            source === "apple_pay"
              ? ""
              : selectedCardId,
          stripe_product_id: transaction.service_details.stripe_product_id,
          source,
          order: orderID,
          user_environment: "",
          service_payment_id: transaction.service_payment_id,
          applied_discounts: {
            entered_promo_code: "",
            promo_value: 0,
            order_total: parseInt(transaction.amount),
            order_total_upfront: 0,
            order_total_installment: 0,
          },
          payment_term: transaction.payment_term,
        })

        if (response?.message === "success") {
          setCardSave(false)
          toast.success("Payment method updated successfully!")
          navigate("/account-settings/payment-center", {
            state: { localPaymentMethods: payments || paymentMethods },
          })
        } else {
          toast.error(
            response?.message || "Something went wrong. Please try again."
          )
        }
      } catch (error: any) {
        console.error("Error in onApprove:", error)
        toast.error(
          error?.response?.data?.message ||
            "Failed to complete purchase. Please try again."
        )
      } finally {
        setProcessingLoading(false)
      }
    },
    [user, transaction, toast, selectedCardId]
  )

  const handleSave = async () => {
    if (!selectedCardId) {
      toast.error("Please select a payment method.")
      return
    }
    await createOrder(selectedCardId, "card")
  }

  useEffect(() => {
    const loadPayPalSDK = () => {
      const existing = document.getElementById(
        "paypal-sdk"
      ) as HTMLScriptElement | null

      if (existing) {
        const url = new URL(existing.src)

        const loadedMerchantId = url.searchParams.get("merchant-id")
        const loadedComponents = url.searchParams.get("components")
        if (
          loadedMerchantId === merchantID &&
          loadedComponents === paypalComponents
        ) {
          setIsSdkReady(true)
          return
        }

        existing.remove()
      }

      const script = document.createElement("script")
      script.id = "paypal-sdk"
      script.src = `https://www.paypal.com/sdk/js?components=${paypalComponents}&client-id=${clientID}&merchant-id=${merchantID}&currency=USD&intent=capture&disable-funding=paylater`
      script.async = true

      script.onload = () => {
        setIsSdkReady(true)
      }
      script.onerror = (err) => {
        console.error("Failed to load PayPal SDK:", err)
      }
      document.body.appendChild(script)
    }
    loadPayPalSDK()
  }, [merchantID])

  if (!isSdkReady) {
    return (
      <div className="flex justify-center items-center w-full h-[300px]">
        <Loader />
      </div>
    )
  }

  const isAPGPmaintenanceInProg =
    process.env.REACT_APP_AP_GP_MAINTENANCE_IN_PROG === "true"

  const isSafari =
    typeof window !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const canShowApplePay =
    isApplePayEnabled &&
    isSafari &&
    typeof (window as any).ApplePaySession !== "undefined"

  const canShowGooglePay = isGooglePayEnabled

  const walletLabel = (() => {
    if (canShowApplePay && canShowGooglePay) {
      return (
        <>
          <span className="font-bold">Apple Pay</span> and{" "}
          <span className="font-bold">Google Pay</span>
        </>
      )
    }

    if (canShowApplePay) {
      return <span className="font-bold">Apple Pay</span>
    }

    if (canShowGooglePay) {
      return <span className="font-bold">Google Pay</span>
    }

    return null
  })()

  return (
    <div className="w-full flex flex-col items-center py-10 px-4 md:px-8 bg-gray-50 min-h-screen -mt-12">
      <div className="relative w-full max-w-[640px] bg-white rounded-[16px] shadow-md flex flex-col items-center overflow-y-auto">
        <div className="w-full pt-[60px] px-[24px] md:px-[48px] text-center">
          <h1 className="text-black text-[22px] md:text-[26px] font-semibold mb-[8px]">
            Complete Your Payment
          </h1>
          <p className="text-grayCharcoal text-[14px] mb-[20px] leading-snug max-w-[520px] mx-auto">
            You have an outstanding payment for{" "}
            <strong>{transaction?.description}</strong> — total amount:{" "}
            <strong>${parseFloat(transaction?.amount || 0).toFixed(2)}</strong>.
          </p>
        </div>

        <div className="w-full flex flex-col items-center mb-6">
          {(!payments || payments.length === 0) && loadingPaymentMethods ? (
            <div className="flex justify-center items-center h-[200px]">
              <AnimatedTextLoader text="Loading your payment options..." />
            </div>
          ) : (
            <>
              <div className="flex justify-center border-b border-grayMist p-2 mb-4 w-full max-w-[440px] bg-transparent">
                <h3 className="text-[16px] font-semibold text-center w-full">
                  Select a Payment Method
                </h3>
              </div>
              {cardSave && (
                <div className="flex justify-start py-10 z-10 relative sm:px-6 px-3 items-center gap-2">
                  <span className="italic text-sm text-gray-600">
                    It may take a few minutes for your card to appear.
                  </span>
                </div>
              )}
              <div className="w-full max-w-[440px] flex flex-col gap-2">
                {(!cardSave
                  ? payments && payments.length > 0
                    ? payments
                    : paymentMethods
                  : paymentMethods || payments
                ).map((card: any) => (
                  <label
                    key={card.id}
                    htmlFor={`card-${card.id}`}
                    className={`grid grid-cols-[auto_1fr_2fr_1fr] items-center border rounded-lg px-3 py-2 shadow-sm text-[14px] cursor-pointer transition-all duration-150 ${
                      selectedCardId === card.id
                        ? "border-blurple bg-indigo-50"
                        : "border-grayMist bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <input
                      id={`card-${card.id}`}
                      type="radio"
                      name="selectedCard"
                      checked={selectedCardId === card.id}
                      onChange={() => setSelectedCardId(card.id)}
                      className="w-4 h-4 text-blurple accent-blurple cursor-pointer"
                      disabled={processingLoading}
                    />

                    <span className="font-medium text-gray-800 truncate ml-2">
                      {card.name || "—"}
                    </span>

                    <div className="flex items-center justify-center gap-2 text-gray-700">
                      {(() => {
                        const brand = card.brand?.toLowerCase()
                        const Icon =
                          brand === "visa"
                            ? SvgVisa
                            : brand === "mastercard"
                            ? SvgMastercard
                            : brand === "american express" || brand === "amex"
                            ? SvgAmericanExpress
                            : brand === "discover"
                            ? SvgDiscover
                            : brand === "dinersclub"
                            ? SvgDinersClub
                            : null
                        return Icon ? (
                          <Icon className="w-5 h-5 shrink-0" />
                        ) : null
                      })()}
                      <span>•••• {card.last_digits}</span>
                    </div>

                    <span className="text-center text-gray-600">
                      {card.expiry ? card.expiry.replace("-", "/") : "—"}
                    </span>
                  </label>
                ))}
                <label
                  className={`flex items-center justify-between border rounded-lg px-3 py-3 shadow-sm cursor-pointer transition-all duration-150 ${
                    selectedCardId === "new"
                      ? "border-blurple bg-indigo-50"
                      : "border-grayMist bg-gray-50 hover:bg-gray-100"
                  }`}
                  onClick={() => {
                    setSelectedCardId("new")
                    setShowNewCardModal(true)
                  }}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      aria-label="Add New Card"
                      name="selectedCard"
                      checked={selectedCardId === "new"}
                      readOnly
                      className="w-4 h-4 text-blurple accent-blurple cursor-pointer"
                    />
                    <span className="font-medium text-gray-800">
                      Add New Card
                    </span>
                  </div>
                </label>
                <div>
                  {isAPGPmaintenanceInProg && walletLabel && (
                    <div className="w-full mb-2 rounded-md bg-[#FF4A00] px-3 py-2 text-center">
                      <span className="text-base text-white">
                        {walletLabel}{" "}
                        {canShowApplePay && canShowGooglePay ? "are" : "is"}{" "}
                        currently undergoing maintenance and may be temporarily
                        unavailable.
                      </span>
                    </div>
                  )}
                  <div className="mb-1.5">
                    <PayPalGooglePay
                      createOrder={createOrder}
                      onApprove={onApprove}
                      isSdkReady={isSdkReady}
                      orderTotalRef={transaction.amount}
                    />
                  </div>
                  <PayPalApplePay
                    createOrder={createOrder}
                    onApprove={onApprove}
                    isSdkReady={isSdkReady}
                    reactiveOrderTotal={parseFloat(transaction.amount)}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {selectedCardId === "" ? (
          <div className="w-full flex flex-col items-center text-center rounded-lg p-4 -mt-2">
            <div className="w-full max-w-[440px] mb-6">
              <div className="justify-flex flex-col items-center justify-center">
                <PayPalButton createOrder={createOrder} onApprove={onApprove} />
              </div>

              <div className="relative flex items-center justify-center w-full my-4 -mb-4">
                <div className="flex-grow border-t border-grayMist"></div>
                <span className="px-3 text-graySlate text-sm">OR</span>
                <div className="flex-grow border-t border-grayMist"></div>
              </div>
            </div>

            <div className="w-full max-w-[420px]">
              <div className="mb-[12px] flex flex-col items-center">
                <img
                  src="https://www.paypalobjects.com/webstatic/mktg/logo/AM_mc_vs_dc_ae.jpg"
                  alt="Payments by PayPal"
                  className="h-10 w-auto"
                />
                <p className="text-grayCharcoal text-[12px] mt-1 leading-tight">
                  Secured by PayPal — your card details stay private.
                </p>
              </div>

              <p className="text-[13px] mb-[16px] max-w-[360px] leading-snug mx-auto">
                Please enter the cardholder name and card details below.
              </p>

              <PayPalCardFields
                isCheckedRef={isCheckedRef}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                createOrder={createOrder}
                onApprove={onApprove}
                isTermsAgreed={isTermsAgreed}
                setIsTermsAgreed={setIsTermsAgreed}
                isSubx={false}
                activeService=""
                setShowModal={() =>
                  navigate("/account-settings/payment-center", {
                    state: { localPaymentMethods: payments || paymentMethods },
                  })
                }
                payNow={true}
              />
            </div>
          </div>
        ) : (
          <div className="w-full gap-4 flex justify-center mb-8">
            <Button
              className="w-1/4"
              onClick={handleSave}
              disabled={processingLoading}
            >
              {processingLoading ? <Loader /> : "Pay Now"}
            </Button>

            <Button
              className="w-1/4"
              onClick={() =>
                navigate("/account-settings/payment-center", {
                  state: { localPaymentMethods: payments || paymentMethods },
                })
              }
              variant="outline"
              disabled={processingLoading}
            >
              Cancel
            </Button>
          </div>
        )}

        <p className="text-graySlate text-[12px] text-center mt-[16px] leading-tight max-w-[460px] mb-8">
          By processing this payment, you agree to Zoee’s{" "}
          <a
            href="/terms-and-conditions"
            target="_blank"
            className="text-blurple"
          >
            Agreement & Disclosures
          </a>
          , our{" "}
          <a
            href="https://intercom.help/zoee/en/articles/8623117-how-does-zoee-protect-your-information-on-paypal"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blurple"
          >
            Payment Authorization
          </a>{" "}
          terms, and PayPal’s{" "}
          <a
            href="https://www.paypal.com/us/webapps/mpp/ua/useragreement-full"
            target="_blank"
            className="text-blurple"
          >
            User Agreement
          </a>
        </p>
      </div>
      {showNewCardModal && (
        <NewCardModal
          setShowNewCardModal={setShowNewCardModal}
          onCardAdded={() => {
            getPaymentMethods()
            setSelectedCardId(null)
            navigate(".", { replace: true })
          }}
          setCardSave={setCardSave}
        />
      )}
    </div>
  )
}

export default PayNowPage
